// src/components/FileUploader.js
import React, { useRef } from "react";
import { Typography, Button } from "@mui/material";
import { CloudUploadOutlined } from "@mui/icons-material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CancelIcon from "@mui/icons-material/Cancel";
import ArgonBox from "components/ArgonBox";
import PropTypes from "prop-types";

const FileUploader = ({
  label = "Upload File",
  files = [],
  setFiles,
  accept = "*/*",
  multiple = true,
  Width = "100%", // 👈 default width
  Height = 150, // 👈 default height
}) => {
  const fileInputRef = useRef(null);

  // 📌 Handle Drop
  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    console.log("FileUploader: Files dropped:", droppedFiles);
    handleFiles(droppedFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // 📌 File Picker
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    console.log("FileUploader: Files selected:", selectedFiles);
    handleFiles(selectedFiles);
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // 📌 Process Files
  const handleFiles = (newFiles) => {
    if (!Array.isArray(newFiles)) return;
    const updatedFiles = newFiles.map((file) => ({
      file,
      preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
    }));
    setFiles((prev) => {
      const prevArray = Array.isArray(prev) ? prev : [];
      let newArray;
      if (multiple) {
        newArray = [...prevArray, ...updatedFiles];
      } else {
        // For single file upload, replace existing files
        newArray = updatedFiles.length > 0 ? [updatedFiles[0]] : [];
      }
      console.log("FileUploader: Setting files to:", newArray);
      return newArray;
    });
  };

  // 📌 Remove File
  const removeFile = (index) => {
    setFiles((prev) => {
      const prevArray = Array.isArray(prev) ? prev : [];
      const newArray = prevArray.filter((_, i) => i !== index);
      console.log("FileUploader: Removing file at index", index, "new array:", newArray);
      return newArray;
    });
  };

  return (
    <ArgonBox sx={{ width: "100%", maxWidth: 500 }}>
      {/* Label */}
      <Typography variant="body2" fontWeight="medium" mb={1}>
        {label}
      </Typography>

      {/* Upload Box */}
      <ArgonBox
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          width: Width, // 👈 props se width
          height: Height, // 👈 props se height
          border: "2px dashed #b0b0b0",
          borderRadius: "10px",
          p: 3,
          textAlign: "center",
          backgroundColor: "#fafafa",
          cursor: "pointer",
          display: "flex", // 👈 content ko center align karne ke liye
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          "&:hover": { borderColor: "#1976d2", background: "#f0f8ff" },
        }}
      >
        <CloudUploadOutlined sx={{ fontSize: 32, color: "#777", mb: 1 }} />
        <Typography variant="body2">
          Drop files here or <span style={{ color: "#1976d2", fontWeight: 500 }}>browse</span>
        </Typography>
        <Typography variant="caption" color="textSecondary" display="block" mt={1}>
          Max size: 50MB
        </Typography>

        {/* Hidden Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
      </ArgonBox>

      {/* Preview Section */}
      {Array.isArray(files) && files.length > 0 && (
        <ArgonBox display="flex" flexWrap="wrap" gap={2} mt={2}>
          {Array.isArray(files) &&
            files.map((item, index) => (
              <ArgonBox key={index} textAlign="center" sx={{ position: "relative" }}>
                {item.preview ? (
                  <ArgonBox
                    sx={{
                      width: 80,
                      height: 80,
                      border: "1px solid #ddd",
                      borderRadius: 2,
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={item.preview}
                      alt={`preview-${index}`}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  </ArgonBox>
                ) : (
                  <ArgonBox
                    sx={{
                      width: 80,
                      height: 80,
                      border: "1px solid #ddd",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#f5f5f5",
                    }}
                  >
                    <InsertDriveFileIcon sx={{ fontSize: 40, color: "#555" }} />
                  </ArgonBox>
                )}

                {/* Delete Button */}
                <Button
                  onClick={() => removeFile(index)}
                  sx={{
                    position: "absolute",
                    top: -8,
                    right: -8,
                    minWidth: 0,
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    background: "#fff",
                    "&:hover": { background: "#eee" },
                  }}
                >
                  <CancelIcon sx={{ color: "red", fontSize: 20 }} />
                </Button>

                {/* File Name */}
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ maxWidth: 80, display: "block", mt: 0.5 }}
                >
                  {item.file.name}
                </Typography>
              </ArgonBox>
            ))}
        </ArgonBox>
      )}
    </ArgonBox>
  );
};

FileUploader.propTypes = {
  label: PropTypes.string,
  files: PropTypes.array.isRequired,
  setFiles: PropTypes.func.isRequired,
  accept: PropTypes.string,
  multiple: PropTypes.bool,
  Width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), // 👈 number ya % dono chalega
  Height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

FileUploader.defaultProps = {
  label: "Upload File",
  files: [],
  accept: "*/*",
  multiple: true,
  // default 150px height
};

export default FileUploader;

// // src/components/FileUploader.js
// import React from "react";
// import { Typography, Button } from "@mui/material";
// import { CloudUploadOutlined } from "@mui/icons-material";
// import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
// import CancelIcon from "@mui/icons-material/Cancel";
// import ArgonBox from "components/ArgonBox";
// import PropTypes from "prop-types";

// const FileUploader = ({ label = "Upload File", files = [], setFiles, accept = "*/*",  multiple = true,Width,Height }) => {

//   // 📌 Handle Drop
//   const handleDrop = (e) => {
//     e.preventDefault();
//     const droppedFiles = Array.from(e.dataTransfer.files);
//     handleFiles(droppedFiles);
//   };

//   const handleDragOver = (e) => {
//     e.preventDefault();
//   };

//   // 📌 File Picker
//   const handleFileChange = (e) => {
//     const selectedFiles = Array.from(e.target.files);
//     handleFiles(selectedFiles);
//   };

//   // 📌 Process Files
//   const handleFiles = (newFiles) => {
//     const updatedFiles = newFiles.map((file) => ({
//       file,
//       preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
//     }));
//     setFiles((prev) => [...prev, ...updatedFiles]);
//   };

//   // 📌 Remove File
//   const removeFile = (index) => {
//     setFiles((prev) => prev.filter((_, i) => i !== index));
//   };

//   return (
//     <ArgonBox sx={{ width: "100%", maxWidth: 500 }}>
//       {/* Label */}
//       <Typography variant="body2" fontWeight="medium" mb={1}>
//         {label}
//       </Typography>

//       {/* Upload Box */}
//       <ArgonBox
//         onDrop={handleDrop}
//         onDragOver={handleDragOver}
//         onClick={() => document.getElementById(`${label}-input`).click()}
//         sx={{
//           border: "2px dashed #b0b0b0",
//           borderRadius: "10px",
//           p: 3,
//           textAlign: "center",
//           backgroundColor: "#fafafa",
//           cursor: "pointer",
//           "&:hover": { borderColor: "#1976d2", background: "#f0f8ff" },
//         }}
//       >
//         <CloudUploadOutlined sx={{ fontSize: 32, color: "#777", mb: 1 }} />
//         <Typography variant="body2">
//           Drop files here or{" "}
//           <span style={{ color: "#1976d2", fontWeight: 500 }}>browse</span>
//         </Typography>
//         <Typography variant="caption" color="textSecondary" display="block" mt={1}>
//           Max size: 50MB
//         </Typography>

//         {/* Hidden Input */}
//         <input
//           id={`${label}-input`}
//           type="file"
//           accept={accept}
//           multiple={multiple}
//           onChange={handleFileChange}
//           style={{ display: "none" }}
//         />
//       </ArgonBox>

//       {/* Preview Section */}
//       {files.length > 0 && (
//         <ArgonBox display="flex" flexWrap="wrap" gap={2} mt={2}>
//           {files.map((item, index) => (
//             <ArgonBox key={index} textAlign="center" sx={{ position: "relative" }}>
//               {item.preview ? (
//                 <ArgonBox
//                   sx={{
//                     width: 80,
//                     height: 80,
//                     border: "1px solid #ddd",
//                     borderRadius: 2,
//                     overflow: "hidden",
//                   }}
//                 >
//                   <img
//                     src={item.preview}
//                     alt={`preview-${index}`}
//                     style={{ width: "100%", height: "100%", objectFit: "cover" }}
//                   />
//                 </ArgonBox>
//               ) : (
//                 <ArgonBox
//                   sx={{
//                     width: 80,
//                     height: 80,
//                     border: "1px solid #ddd",
//                     borderRadius: 2,
//                     display: "flex",
//                     alignItems: "center",
//                     justifyContent: "center",
//                     background: "#f5f5f5",
//                   }}
//                 >
//                   <InsertDriveFileIcon sx={{ fontSize: 40, color: "#555" }} />
//                 </ArgonBox>
//               )}

//               {/* Delete Button */}
//               <Button
//                 onClick={() => removeFile(index)}
//                 sx={{
//                   position: "absolute",
//                   top: -8,
//                   right: -8,
//                   minWidth: 0,
//                   width: 24,
//                   height: 24,
//                   borderRadius: "50%",
//                   background: "#fff",
//                   "&:hover": { background: "#eee" },
//                 }}
//               >
//                 <CancelIcon sx={{ color: "red", fontSize: 20 }} />
//               </Button>

//               {/* File Name */}
//               <Typography
//                 variant="caption"
//                 noWrap
//                 sx={{ maxWidth: 80, display: "block", mt: 0.5 }}
//               >
//                 {item.file.name}
//               </Typography>
//             </ArgonBox>
//           ))}
//         </ArgonBox>
//       )}
//     </ArgonBox>
//   );
// };

// FileUploader.propTypes = {
//   label: PropTypes.string,
//   files: PropTypes.array.isRequired,       // files array (required)
//   setFiles: PropTypes.func.isRequired,     // state setter (required)
//   accept: PropTypes.string,                // file types (optional)
//   multiple: PropTypes.bool,                // single/multiple (optional)
// };

// FileUploader.defaultProps = {
//   label: "Upload File",
//   files: [],
//   accept: "*/*",
//   multiple: true,
// };

// export default FileUploader;
